import { SectionTitle } from "./SectionTitle";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";
import { Star, Quote } from "lucide-react";
import { motion } from "framer-motion";
import { fadeUp, viewportOnce } from "@/lib/animations";

interface Testimonial {
  id: number;
  name: string;
  role: string;
  initials: string;
  rating: number;
  review: string;
  product: string;
}

const testimonialsRow1: Testimonial[] = [
  {
    id: 1,
    name: "Priya Sharma",
    role: "Verified Buyer",
    initials: "PS",
    rating: 5,
    review:
      "Absolutely in love with my silver band ring! The purity is exactly as described — I got it verified at a local jeweller and it passed 100%. Great quality at a very fair price.",
    product: "Silver Band Ring",
  },
  {
    id: 2,
    name: "Anjali Mehta",
    role: "Verified Buyer",
    initials: "AM",
    rating: 5,
    review:
      "The twisted rope ring is stunning. Wore it to a wedding and got so many compliments. Packaging was premium and delivery was super fast. Will definitely order again!",
    product: "Silver Twisted Ring",
  },
  {
    id: 3,
    name: "Sneha Patel",
    role: "Verified Buyer",
    initials: "SP",
    rating: 5,
    review:
      "I was sceptical ordering silver online but Smora proved me wrong. The quality is exactly as described and the ring feels premium. Love that they price based on live rates — very honest.",
    product: "Silver Solitaire Ring",
  },
  {
    id: 4,
    name: "Kavita Nair",
    role: "Verified Buyer",
    initials: "KN",
    rating: 5,
    review:
      "Gifted this to my sister on her birthday and she was overjoyed. The ring came in a beautiful box, quality is top notch. Finish is flawless. Highly recommend!",
    product: "Silver Statement Ring",
  },
];

const testimonialsRow2: Testimonial[] = [
  {
    id: 5,
    name: "Ritu Agarwal",
    role: "Verified Buyer",
    initials: "RA",
    rating: 5,
    review:
      "Ordered the silver oxidised ring and it looks exactly like in the photos. Very sturdy and doesn't tarnish easily. Already bought a second one for my friend.",
    product: "Silver Oxidised Ring",
  },
  {
    id: 6,
    name: "Deepika Singh",
    role: "Verified Buyer",
    initials: "DS",
    rating: 5,
    review:
      "The price-to-quality ratio is unbeatable. I compared the rate with local market silver prices and Smora charges absolutely fair. Very transparent about making charges too.",
    product: "Silver Band Ring",
  },
  {
    id: 7,
    name: "Meena Krishnan",
    role: "Verified Buyer",
    initials: "MK",
    rating: 5,
    review:
      "Bought three rings at once and all three are perfect. Sizes fit exactly as mentioned. Customer support was helpful when I had a query. Will be a regular customer!",
    product: "Silver Twisted Ring",
  },
  {
    id: 8,
    name: "Pooja Verma",
    role: "Verified Buyer",
    initials: "PV",
    rating: 5,
    review:
      "Smora is my go-to for silver rings now. Each ring is beautifully finished with no rough edges. The 7-day return policy gives me confidence to try new styles too.",
    product: "Silver Solitaire Ring",
  },
];

function TestimonialCard({ t }: { t: Testimonial }) {
  return (
    <div className="w-[80vw] sm:w-[360px] bg-background border border-border rounded-2xl p-5 flex flex-col gap-3 h-full">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 text-primary text-sm font-bold flex items-center justify-center shrink-0">
            {t.initials}
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground">{t.name}</p>
            <p className="text-xs text-muted-foreground">{t.role}</p>
          </div>
        </div>
        <Quote className="h-5 w-5 text-primary/30 shrink-0 mt-1" />
      </div>

      <div className="flex gap-0.5">
        {Array.from({ length: t.rating }).map((_, i) => (
          <Star key={i} className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
        ))}
      </div>

      <p className="text-sm text-muted-foreground leading-relaxed flex-1">
        "{t.review}"
      </p>

      <div className="pt-2 border-t border-border">
        <span className="text-xs text-primary font-medium bg-primary/10 px-2.5 py-1 rounded-full">
          {t.product}
        </span>
      </div>
    </div>
  );
}

function TestimonialSlider({
  testimonials,
  reverse = false,
  id,
}: {
  testimonials: Testimonial[];
  reverse?: boolean;
  id: string;
}) {
  return (
    <div className="relative">
      <div className="absolute left-0 top-0 bottom-0 w-[10vw] bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-[10vw] bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />
      <Swiper
        modules={[Autoplay]}
        autoplay={{ delay: 0, disableOnInteraction: false, reverseDirection: reverse }}
        speed={6000}
        loop={true}
        slidesPerView="auto"
        spaceBetween={20}
        className={id}
      >
        {[...testimonials, ...testimonials].map((t, i) => (
          <SwiperSlide key={`${id}-${i}`} style={{ width: "auto", height: "auto" }}>
            <TestimonialCard t={t} />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}

export function TestimonialsSection() {
  return (
    <section id="testimonials" className="py-10 md:py-14 lg:py-16 overflow-hidden">
      <motion.div
        className="mb-10 md:mb-12 wrapper"
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
      >
        <SectionTitle
          badge="REVIEWS"
          title="What Our Customers Say"
          subtitle="Thousands of happy customers across India trust Smora for their silver ring purchases."
          centered
        />
      </motion.div>

      <div className="space-y-4">
        <TestimonialSlider testimonials={testimonialsRow1} reverse={false} id="row1" />
        <TestimonialSlider testimonials={testimonialsRow2} reverse={true} id="row2" />
      </div>
    </section>
  );
}
